import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoIcon from '@mui/icons-material/Info';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

const MENU_ITEMS = [
  { path: '/Advanced', text: 'Advance Settings' },
  { path: '/Simregbot', text: 'Simregbot Manager' },
  { path: '/', text: 'Goip Status' },
  { path: '/GoipStats', text: 'Goip Statistics' },
  { path: '/RegBy', text: 'RegBy' },
  { path: '/SMS_Receive', text: 'SMS Receive Logs' },
  { path: '/SMS_Logs', text: 'SMS Logs' },
  { path: '/Call_Logs', text: 'Call Logs' },
  { path: '/Snapshot', text: 'Snapshot' },
  { path: '/Loader', text: 'Loader' },
  { path: '/GoipManagement_offline', text: 'Goip Management Desktop' },
  { path: '/Scheduler', text: 'Batch Scheduler' },
  { path: '/Bridge', text: 'Bridge Manager' },
  { path: '/OtpServices', text: 'OTP Services Manager' },
  { path: '/SmsVoipManager', text: 'SMS VOIP Manager' },
  { path: '/VoipReportTool', text: 'VOIP Report Tool' },
  { path: '/CatchedNumbers', text: 'Catched Numbers' },
  { path: '/G9_Ports_Voip', text: 'Goip9 Voip' },
  { path: '/Fintracker', text: 'Financial Tracker' },
  { path: '/NotificationReceived', text: 'Notification Data' },
];

const ACRONYMS = [
  { acronym: 'CC1', meaning: 'Edz-route-63cc' },
  { acronym: 'CC2', meaning: 'Edz-route-a63cc' },
  { acronym: 'CC3', meaning: 'Frek-route-63cc' },
  { acronym: 'CC4', meaning: 'Frek-route-a63cc' },
  { acronym: 'CC5', meaning: 'Edz-route-b63cc' },
  { acronym: 'CC6', meaning: 'Frek-route-b63cc' },
  { acronym: 'CC7', meaning: 'Frek-route-c63cc' },
  { acronym: 'CC8', meaning: 'Edz-route-c63cc' },
  { acronym: 'CC9', meaning: 'Edz-route-d63cc' },
  { acronym: 'CC10', meaning: 'Frek-route-d63cc' },
  { acronym: 'CC11', meaning: 'Edz-route-e63cc' },
  { acronym: 'CC12', meaning: 'Frek-route-e63cc' },
  { acronym: 'CC13', meaning: 'Edz-route-f63cc' },
  { acronym: 'CC14', meaning: 'Frek-route-f63cc' },
];

const CHECKLIST_GROUPS = [
  { key: 'g1', label: 'Goip1', value: '1' },
  { key: 'g2', label: 'Goip2', value: '2' },
  { key: 'g3', label: 'Goip3', value: '3' },
  { key: 'g4', label: 'Goip4', value: '4' },
  { key: 'g5', label: 'Goip5', value: '5' },
  { key: 'g6', label: 'Goip6', value: '6' },
  { key: 'g7', label: 'Goip7', value: '7' },
  { key: 'g8', label: 'Goip8', value: '8' },
];

const EXPIRY_CHECKLIST_URL = 'https://frek.cc/proxy/38.54.86.24:5000/expiry/64746278498';

function SideDrawer({ items, onMenuItemClick, open, onClose, currentPath }) {
  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 280,
          height: '200%',
          background:
            'linear-gradient(180deg, rgba(12,12,18,0.98) 0%, rgba(14,16,24,0.97) 100%)',
          color: '#e8e9ee',
          p: 1.5,
        }}
        role="presentation"
      >
        <Stack spacing={1}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        </Stack>
        <List sx={{ mt: 1 }}>
          {items.map((item) => {
            const isActive = currentPath === item.path;
            return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => {
                onMenuItemClick(item.text);
                onClose();
              }}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                color: isActive ? '#ffffff' : 'rgba(255,255,255,0.9)',
                backgroundColor: isActive ? 'rgba(90, 150, 255, 0.22)' : 'transparent',
                '&:hover': {
                  backgroundColor: isActive
                    ? 'rgba(90, 150, 255, 0.3)'
                    : 'rgba(255,255,255,0.08)',
                },
              }}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 600,
                }}
              />
            </ListItemButton>
          )})}
        </List>
      </Box>
    </Drawer>
  );
}

function MyAppBar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [pageTitle, setPageTitle] = useState(
    localStorage.getItem('pageTitle') || 'Dashboard for Manager Version 3.0'
  );
  const isReloadly = pageTitle === 'Reloadly';
  const [drawerOpen, setDrawerOpen] = useState(false);

  const clearTopUpResults = () => {
    localStorage.removeItem('topUpResults');
  };

  useEffect(() => {
    const currentItem = MENU_ITEMS.find(item => item.path === location.pathname);
    if (currentItem) {
      setPageTitle(currentItem.text);
      localStorage.setItem('pageTitle', currentItem.text);
    }
  }, [location.pathname]);

  const handleMenuItemClick = (text) => {
    setPageTitle(text);
    localStorage.setItem('pageTitle', text);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
    window.location.reload();
  };

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const openLogoutDialog = () => setLogoutDialogOpen(true);
  const closeLogoutDialog = () => setLogoutDialogOpen(false);

  const [clearExpiryDialogOpen, setClearExpiryDialogOpen] = useState(false);
  const openClearExpiryDialog = () => setClearExpiryDialogOpen(true);
  const closeClearExpiryDialog = () => setClearExpiryDialogOpen(false);

  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [checklistError, setChecklistError] = useState('');
  const [selectedChecklistValues, setSelectedChecklistValues] = useState([]);

  const openChecklistDialog = () => setChecklistDialogOpen(true);
  const closeChecklistDialog = () => setChecklistDialogOpen(false);

  const handleClearExpiryData = () => {
    localStorage.removeItem('expiryData');
    closeClearExpiryDialog();
  };

  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const openInfoDialog = () => setInfoDialogOpen(true);
  const closeInfoDialog = () => setInfoDialogOpen(false);

  const [notificationCount, setNotificationCount] = useState(0);
  const [popupMessages, setPopupMessages] = useState([]);

  useEffect(() => {
    const messages = JSON.parse(localStorage.getItem('popupMessages') || '[]');
    setPopupMessages(messages);
    setNotificationCount(messages.length);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const messages = JSON.parse(localStorage.getItem('popupMessages') || '[]');
      setPopupMessages(messages);
      setNotificationCount(messages.length);
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!checklistDialogOpen) {
      return;
    }

    const fetchChecklist = async () => {
      setChecklistLoading(true);
      setChecklistError('');
      try {
        const response = await fetch(EXPIRY_CHECKLIST_URL, { method: 'GET' });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        const rawValue = Array.isArray(data) && data[0] && data[0].value ? data[0].value : '';
        const parsedValues = String(rawValue)
          .split(',')
          .map((item) => item.trim())
          .filter((value) => value !== '' && CHECKLIST_GROUPS.some((group) => group.value === value));
        setSelectedChecklistValues(parsedValues);
      } catch (error) {
        setChecklistError('Failed to load checklist values.');
      } finally {
        setChecklistLoading(false);
      }
    };

    fetchChecklist();
  }, [checklistDialogOpen]);

  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const openNotificationsDialog = () => setNotificationsDialogOpen(true);
  const closeNotificationsDialog = () => setNotificationsDialogOpen(false);

  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const openClearDialog = () => setClearDialogOpen(true);
  const closeClearDialog = () => setClearDialogOpen(false);

  const clearPopupMessages = () => {
    localStorage.setItem('popupMessages', JSON.stringify([]));
    setPopupMessages([]);
    setNotificationCount(0);
    setClearDialogOpen(false);
  };

  const toggleChecklistValue = (value) => {
    setSelectedChecklistValues((prevValues) => {
      if (prevValues.includes(value)) {
        return prevValues.filter((item) => item !== value);
      }
      return [...prevValues, value];
    });
  };

  const handleSaveChecklist = async () => {
    setChecklistSaving(true);
    setChecklistError('');
    try {
      const payload = {
        value: selectedChecklistValues
          .slice()
          .sort((a, b) => Number(a) - Number(b))
          .join(','),
      };
      const response = await fetch(EXPIRY_CHECKLIST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      closeChecklistDialog();
    } catch (error) {
      setChecklistError('Failed to save checklist values.');
    } finally {
      setChecklistSaving(false);
    }
  };

  const infoRows = useMemo(() => ACRONYMS, []);

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background:
            'linear-gradient(135deg, rgba(18,18,26,0.96) 0%, rgba(20,22,32,0.94) 45%, rgba(16,17,24,0.98) 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: '46px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        > 
          <Tooltip title="Open navigation">
            <IconButton
              size="small"
              edge="start"
              color="inherit"
              aria-label="open navigation"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              component="div"
              sx={{ fontWeight: 700, color: '#e8e9ee', whiteSpace: 'nowrap' }}
            >
              {pageTitle}
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

          </Stack>

          <Box sx={{ flex: 1 }} />

          {isReloadly ? (
            <Button
              size="small"
              variant="outlined"
              onClick={clearTopUpResults}
              sx={{
                borderColor: 'rgba(255,255,255,0.25)',
                color: 'rgba(255,255,255,0.85)',
                textTransform: 'none',
                fontSize: 12,
              }}
            >
              Reset Results
            </Button>
          ) : null}

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Goip Opener">
              <IconButton
                color="inherit"
                onClick={openChecklistDialog}
                aria-label="open checklist"
              >
                <FactCheckIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear Expiry Data">
              <IconButton color="inherit" onClick={openClearExpiryDialog} aria-label="clear expiry data">
                <AutorenewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Information">
              <IconButton color="inherit" onClick={openInfoDialog} aria-label="system information">
                <InfoIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout">
              <IconButton color="inherit" onClick={openLogoutDialog} aria-label="logout">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <SideDrawer
        items={MENU_ITEMS}
        onMenuItemClick={handleMenuItemClick}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentPath={location.pathname}
      />

      <Dialog
        open={infoDialogOpen}
        onClose={closeInfoDialog}
        fullWidth
        maxWidth="md"
        aria-labelledby="info-dialog-title"
      >
        <DialogTitle id="info-dialog-title">System Information</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom component="div" sx={{ mt: 2 }}>
            Acronyms and Definitions
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="acronyms table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Acronym</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Meaning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {infoRows.map((row) => (
                  <TableRow key={row.acronym}>
                    <TableCell>{row.acronym}</TableCell>
                    <TableCell>{row.meaning}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInfoDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={checklistDialogOpen}
        onClose={closeChecklistDialog}
        aria-labelledby="checklist-dialog-title"
        aria-describedby="checklist-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="checklist-dialog-title">Goip Opener</DialogTitle>
        <DialogContent>
          <DialogContentText id="checklist-dialog-description">
            Select which goip should be enabled.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            {checklistLoading ? (
              <Typography variant="body2">Loading checklist values...</Typography>
            ) : (
              <List dense>
                {CHECKLIST_GROUPS.map((group) => {
                  const isChecked = selectedChecklistValues.includes(group.value);
                  return (
                    <ListItemButton
                      key={group.key}
                      onClick={() => toggleChecklistValue(group.value)}
                      sx={{ borderRadius: 1 }}
                    >
                      <Checkbox checked={isChecked} tabIndex={-1} disableRipple />
                      <ListItemText primary={group.label} />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
            {checklistError ? (
              <Typography variant="body2" sx={{ color: '#ffb4b4', mt: 1 }}>
                {checklistError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChecklistDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveChecklist}
            color="primary"
            disabled={checklistLoading || checklistSaving}
          >
            {checklistSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearExpiryDialogOpen}
        onClose={closeClearExpiryDialog}
        aria-labelledby="clear-expiry-dialog-title"
        aria-describedby="clear-expiry-dialog-description"
      >
        <DialogTitle id="clear-expiry-dialog-title">Clear Expiry Data</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-expiry-dialog-description">
            Are you sure you want to clear the expiry data?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearExpiryDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleClearExpiryData} color="primary" autoFocus>
            Clear Data
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notificationsDialogOpen}
        onClose={closeNotificationsDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Notifications</DialogTitle>
        <Table>
          <TableRow>
            <TableCell>Message</TableCell>
            <TableCell>Date Time</TableCell>
          </TableRow>
          <TableBody>
            {popupMessages.slice().reverse().map((item, index) => (
              <TableRow key={index}>
                <TableCell id={`message-${index}`}>
                  {item.message}
                </TableCell>
                <TableCell>
                  {item.dateTime}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <DialogActions>
          <Button onClick={openClearDialog} color="primary">
            Clear
          </Button>
          <Button onClick={closeNotificationsDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearDialogOpen}
        onClose={closeClearDialog}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title">Clear Notifications</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-dialog-description">
            Are you sure you want to clear all notifications?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={clearPopupMessages} color="primary" autoFocus>
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={logoutDialogOpen}
        onClose={closeLogoutDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLogoutDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogout} color="primary" autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MyAppBar;
